import { Link } from "react-router-dom";
import { Camera, Shield, AlertCircle, CheckCircle, FileText, Lock, Smartphone, Mail } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from '../components/SEO';

const SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  'name': 'Mobbing Beweise sichern: Schritt-für-Schritt-Anleitung',
  'description': 'So sicherst du Beweise bei Mobbing richtig: Screenshots, E-Mails, Zeugen, Aufnahmen. Was vor Gericht zählt und was nicht.',
  'step': [
    { '@type': 'HowToStep', 'name': 'Sofort handeln', 'text': 'Sichere Beweise unmittelbar nach dem Vorfall — Plattformen können Inhalte löschen.' },
    { '@type': 'HowToStep', 'name': 'Screenshots mit Metadaten', 'text': 'Mache Screenshots, die Datum, Uhrzeit, Absender und Kontext zeigen.' },
    { '@type': 'HowToStep', 'name': 'Mehrfach sichern', 'text': 'Speichere Beweise an mindestens zwei verschiedenen Orten.' },
    { '@type': 'HowToStep', 'name': 'Zeugen ansprechen', 'text': 'Notiere Namen von Personen, die Vorfälle beobachtet haben.' },
    { '@type': 'HowToStep', 'name': 'Meldung einreichen', 'text': 'Lade alle Beweise bei mobbing-melden.de hoch — anonym und kostenlos.' },
  ]
};

const EVIDENCE_CATEGORIES = [
  {
    icon: Smartphone,
    color: "#A0E8AF",
    title: "Digitale Nachrichten",
    items: [
      { label: "WhatsApp / Signal", tip: "Screenshot inkl. Kontaktname, Datum und Uhrzeit sichtbar lassen" },
      { label: "SMS", tip: "Screenshot mit Absendernummer und Zeitstempel" },
      { label: "Instagram / TikTok DMs", tip: "Profilname des Absenders im Bild sichtbar" },
      { label: "Snapchat", tip: "Sofort screenshotten — verschwindet nach Sekunden" },
    ]
  },
  {
    icon: Mail,
    color: "#BDE0FE",
    title: "E-Mails & Dokumente",
    items: [
      { label: "E-Mails", tip: "Vollständigen Header sichern: Von, An, Datum, Betreff, Inhalt" },
      { label: "Abmahnungen / Schreiben", tip: "Original aufbewahren + Kopie einscannen" },
      { label: "Teams / Slack / Discord", tip: "Screenshot und ggf. Export-Funktion nutzen" },
      { label: "Forum-/Kommentar-Posts", tip: "URL + Screenshot — Inhalte können gelöscht werden" },
    ]
  },
  {
    icon: Camera,
    color: "#FFC4D6",
    title: "Fotos & Videos",
    items: [
      { label: "Fotos von Vorfällen", tip: "Metadaten (Exif) enthalten Datum und Ort — nicht bearbeiten" },
      { label: "Videos als Beweis", tip: "Originalaufnahme sichern, nicht zuschneiden oder filtern" },
      { label: "Screenshots von Profilen", tip: "Profilname + URL in der Adresszeile sichtbar" },
      { label: "Aufnahmen am Arbeitsplatz", tip: "Achtung: Heimliche Tonaufnahmen sind in DE strafbar!" },
    ]
  },
  {
    icon: FileText,
    color: "#F6E05E",
    title: "Physische Beweise",
    items: [
      { label: "Briefe / Notizen", tip: "Original im Umschlag aufbewahren — Poststempel als Beweis" },
      { label: "Arztatteste", tip: "Krankschreibungen durch Mobbing-Folgen dokumentieren" },
      { label: "Gesprächsnotizen", tip: "Direkt nach Gesprächen: Datum, Teilnehmer, Inhalt notieren" },
      { label: "Zeugenaussagen", tip: "Namen, Kontakt und was sie gesehen haben notieren" },
    ]
  },
];

const DOS_DONTS = [
  { do: true, text: "Screenshots sofort nach dem Vorfall machen" },
  { do: true, text: "Datum und Uhrzeit immer sichtbar lassen" },
  { do: true, text: "Beweise an mehreren Orten sichern (Handy + Cloud)" },
  { do: true, text: "Originale behalten — nicht nur Kopien" },
  { do: true, text: "Zeugen mit vollständigem Namen notieren" },
  { do: false, text: "Heimliche Tonaufnahmen machen (in DE strafbar!)" },
  { do: false, text: "Screenshots bearbeiten oder zuschneiden" },
  { do: false, text: "Beweise nur auf Firmen-PC oder Schul-iPad speichern" },
  { do: false, text: "Warten — Plattformen können Inhalte löschen" },
  { do: false, text: "Beweise mit dem Täter konfrontieren bevor sie gesichert sind" },
];

const STORAGE_TIPS = [
  { emoji: "☁️", label: "Cloud-Speicher", desc: "Google Drive, iCloud, Dropbox — passwortgeschützt" },
  { emoji: "💾", label: "USB-Stick", desc: "Zuhause sicher aufbewahren — nicht beim Arbeitgeber" },
  { emoji: "📧", label: "Per E-Mail senden", desc: "Screenshots an deine private E-Mail-Adresse schicken" },
  { emoji: "📁", label: "Ordner anlegen", desc: "Pro Monat einen Ordner mit Datum im Namen" },
];

export default function MobbingBeweise() {
  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Header />
      <SEO
        title="Mobbing Beweise sichern: Was vor Gericht zählt (2025)"
        description="So sicherst du Beweise bei Mobbing richtig: Screenshots, E-Mails, Zeugen. Was zählt vor Gericht, was ist strafbar? Kostenloser Ratgeber mit Checkliste."
        path="/mobbing-beweise"
        schema={SCHEMA}
      />

      <main className="px-6 md:px-12 lg:px-24 py-12">
        <div className="max-w-4xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-12">
            <div className="w-20 h-20 bg-[#FFC4D6]/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Shield className="w-10 h-10 text-[#C53030]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4" style={{ fontFamily: 'Nunito' }}>
              Mobbing Beweise richtig sichern
            </h1>
            <p className="text-lg text-[#718096] max-w-2xl mx-auto">
              Beweise sind das Fundament jedes Mobbing-Falls — ob beim Betriebsrat, beim Anwalt oder vor Gericht. Hier erfährst du, was zählt, wie du es sicherst und was du unbedingt vermeiden musst.
            </p>
          </div>

          {/* Urgent note */}
          <div className="bg-[#FFF5F7] border border-[#FFC4D6] rounded-2xl p-6 mb-10 flex gap-4">
            <AlertCircle className="w-6 h-6 text-[#C53030] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-[#C53030] mb-1">Sofort handeln!</p>
              <p className="text-[#4A5568] text-sm">
                Social-Media-Posts, Kommentare und Nachrichten können jederzeit gelöscht werden. Sichere Beweise <strong>unmittelbar nach dem Vorfall</strong> — nicht erst Tage später.
              </p>
            </div>
          </div>

          {/* Evidence categories */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Beweistypen und wie du sie sicherst
            </h2>
            <div className="space-y-6">
              {EVIDENCE_CATEGORIES.map((cat, i) => {
                const Icon = cat.icon;
                return (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100" style={{ backgroundColor: `${cat.color}15` }}>
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${cat.color}30` }}>
                        <Icon className="w-4 h-4" style={{ color: cat.color }} />
                      </div>
                      <h3 className="font-bold text-[#2D3748]">{cat.title}</h3>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {cat.items.map((item, j) => (
                        <div key={j} className="px-6 py-3 flex justify-between gap-4">
                          <span className="font-medium text-[#2D3748] text-sm flex-shrink-0">{item.label}</span>
                          <span className="text-[#718096] text-sm text-right">{item.tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Dos and Don'ts */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Dos & Don'ts bei der Beweissicherung
            </h2>
            <div className="grid md:grid-cols-2 gap-3">
              {DOS_DONTS.map((item, i) => (
                <div key={i} className={`flex items-start gap-3 p-4 rounded-xl text-sm ${item.do ? 'bg-[#E6FFFA]' : 'bg-[#FFF5F7]'}`}>
                  <span className={`font-bold flex-shrink-0 ${item.do ? 'text-[#1A4731]' : 'text-[#C53030]'}`}>
                    {item.do ? '✓' : '✗'}
                  </span>
                  <span className={item.do ? 'text-[#2D4731]' : 'text-[#742A2A]'}>{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Storage tips */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-6" style={{ fontFamily: 'Nunito' }}>
              Beweise sicher aufbewahren
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {STORAGE_TIPS.map((tip, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 flex gap-4">
                  <span className="text-2xl">{tip.emoji}</span>
                  <div>
                    <div className="font-bold text-[#2D3748] mb-1 text-sm">{tip.label}</div>
                    <div className="text-[#718096] text-sm">{tip.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Legal note */}
          <div className="bg-[#EBF8FF] border border-[#BDE0FE] rounded-2xl p-6 mb-10 flex gap-4">
            <Lock className="w-6 h-6 text-[#1A365D] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-[#1A365D] mb-1">Rechtlicher Hinweis: Was in Deutschland erlaubt ist</p>
              <p className="text-[#4A5568] text-sm">
                Screenshots von Nachrichten, die an dich gerichtet sind, sind legal. <strong>Heimliche Tonaufnahmen ohne Zustimmung aller Beteiligten sind strafbar</strong> (§ 201 StGB). Fotos von Personen ohne Einwilligung können das Persönlichkeitsrecht verletzen. Im Zweifel: Anwalt fragen.
              </p>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-br from-[#E6FFFA] to-[#FFF5F7] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-[#2D3748] mb-3" style={{ fontFamily: 'Nunito' }}>
              Beweise gesichert? Jetzt anonym melden.
            </h2>
            <p className="text-[#718096] mb-6 max-w-xl mx-auto">
              Lade deine Screenshots und Beweise direkt hoch — bis zu 50 MB pro Datei, anonym, kostenlos. Wir helfen dir beim nächsten Schritt.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/melden" className="inline-flex items-center justify-center gap-2 bg-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#86D696] transition-all">
                <FileText className="w-5 h-5" />
                Beweise hochladen & melden
              </Link>
              <Link to="/mobbing-protokoll" className="inline-flex items-center justify-center gap-2 border-2 border-[#A0E8AF] text-[#1A4731] font-bold px-8 py-4 rounded-full hover:bg-[#A0E8AF]/20 transition-all">
                Protokoll-Vorlage →
              </Link>
            </div>
          </div>

        </div>
      </main>
      <Footer />
    </div>
  );
}
